import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { OrgCreditBalance } from "@/components/LodgeCredits";

/**
 * Phase DEMO-PROD.2D.2C.1 — Minimal Stripe billing interface (TEST-mode; provider
 * mocked). This component NEVER sends a trusted amount, Stripe Price ID, credit
 * quantity (beyond the accepted pack identifier) or entitlement. It submits ONLY the
 * backend-approved {plan, interval} (subscription) or {pack} (credit pack). The
 * backend remains authoritative for Stripe Price IDs, amounts, credits and access.
 * Displayed prices are informational (HT) only and are never transmitted.
 */

// Display-only catalogue (HT / tax-exclusive). NOT transmitted to the backend.
const PLANS = [
  { key: "starter", name: "Starter", monthly: "99 € HT / mois", annual: "1 009,80 € HT / an" },
  { key: "growth", name: "Growth", monthly: "299 € HT / mois", annual: "3 049,80 € HT / an" },
  { key: "pro", name: "Pro", monthly: "699 € HT / mois", annual: "7 129,80 € HT / an" },
  { key: "premium", name: "Premium", monthly: "1 299 € HT / mois", annual: "13 249,80 € HT / an" },
];

const PACKS = [
  { key: "500", label: "500 crédits", price: "25 € HT" },
  { key: "1000", label: "1 000 crédits", price: "45 € HT" },
  { key: "2500", label: "2 500 crédits", price: "99 € HT" },
  { key: "5000", label: "5 000 crédits", price: "179 € HT" },
  { key: "10000", label: "10 000 crédits", price: "329 € HT" },
];

// Controlled, stable French error copy. We map by the backend's stable error_code and
// NEVER echo raw provider errors, Stripe bodies, identifiers or stack traces.
const ERR_FR = {
  BILLING_NOT_CONFIGURED: "La facturation n'est pas encore configurée. Réessayez plus tard.",
  BILLING_NOT_ALLOWED: "Vous n'êtes pas autorisé à gérer la facturation.",
  BILLING_ACTIVE_SUBSCRIPTION_REQUIRED: "Un abonnement actif est requis pour acheter des crédits.",
  BILLING_INVALID_PLAN: "Offre d'abonnement invalide.",
  BILLING_INVALID_INTERVAL: "Périodicité de facturation invalide.",
  BILLING_INVALID_PACK: "Pack de crédits invalide.",
  BILLING_DEMO_FORBIDDEN: "La facturation n'est pas disponible pour une organisation de démonstration.",
  BILLING_PROVIDER_UNAVAILABLE: "Le service de paiement est momentanément indisponible. Réessayez.",
  BILLING_SUBSCRIPTION_ALREADY_EXISTS: "Un abonnement existe déjà. Gérez-le via « Gérer la facturation ».",
  BILLING_CHECKOUT_IN_PROGRESS: "Une session de paiement est déjà en cours. Patientez quelques instants.",
};

const STATUS_FR = {
  active: "Actif",
  trialing: "Essai (accès non activé)",
  past_due: "Paiement en retard",
  unpaid: "Impayé",
  incomplete: "Incomplet",
  suspended: "Suspendu",
  paused: "En pause",
  canceled: "Annulé",
  cancelled: "Annulé",
  none: "Aucun abonnement",
};

const PLAN_FR = {
  free: "Aucune offre",
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  premium: "Premium",
  enterprise: "Enterprise",
};

function mapError(e) {
  if (e && e.response) {
    const data = e.response.data || {};
    const code = data.error_code;
    if (code && ERR_FR[code]) return ERR_FR[code];
    const status = e.response.status;
    if (status === 403) return ERR_FR.BILLING_NOT_ALLOWED;
    if (status === 404) return "Organisation introuvable.";
    return "Une action de facturation a échoué. Réessayez.";
  }
  // No response object -> network / connectivity failure.
  return "Connexion impossible. Vérifiez votre réseau puis réessayez.";
}

function frDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return null;
  }
}

function StatusRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-700" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function BillingPanel({ orgId, role, isDemo = false, redirectTo }) {
  const doRedirect = useMemo(
    () => redirectTo || ((url) => { window.location.assign(url); }),
    [redirectTo]
  );

  const [status, setStatus] = useState(null); // null=loading, false=error, object=data
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [pending, setPending] = useState(null); // action key currently in-flight
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null); // {type, msg}
  const [refreshKey, setRefreshKey] = useState(0);
  // Synchronous re-entrancy guard: React state updates are async within a tick, so a
  // rapid double-click would otherwise fire two POSTs before `pending` re-renders.
  const inFlightRef = useRef(false);

  const isManager = ["Owner", "Administrator"].includes(role) && !isDemo;
  const subscriptionActive = Boolean(status && status !== true && status.subscription_active);
  // Server-authoritative activation gate. Actions are exposed ONLY when the backend
  // reports billing_available=true. Never inferred from a frontend env var or URL.
  const billingAvailable = Boolean(status && status !== true && status.billing_available);

  // Return-state handling (?billing=success | ?billing=cancel). Never claims a credit
  // grant; only the backend status is authoritative for balances.
  useEffect(() => {
    let b = null;
    try {
      b = new URLSearchParams(window.location.search).get("billing");
    } catch {
      b = null;
    }
    if (b === "success") {
      setBanner({
        type: "success",
        msg: "Paiement confirmé. Votre facturation sera mise à jour automatiquement dès validation par notre prestataire. Le solde ci-dessous se rafraîchit.",
      });
      setRefreshKey((k) => k + 1);
    } else if (b === "cancel") {
      setBanner({ type: "cancel", msg: "Opération annulée. Aucun changement n'a été appliqué." });
    }
    if (b === "success" || b === "cancel") {
      // Neutralize the query param without disrupting routing (path is unchanged).
      try {
        window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      } catch {
        /* no-op */
      }
    }
  }, []);

  // Load subscription + wallet status (Stripe identifiers are never exposed here).
  useEffect(() => {
    if (!orgId) return undefined;
    let alive = true;
    setStatus(null);
    api
      .get(`/organizations/${orgId}/billing/status`)
      .then(({ data }) => { if (alive) setStatus(data); })
      .catch(() => { if (alive) setStatus(false); });
    return () => { alive = false; };
  }, [orgId, refreshKey]);

  const runAction = useCallback(async (key, request) => {
    if (inFlightRef.current) return; // one click -> at most one POST
    inFlightRef.current = true;
    setPending(key);
    setError(null);
    try {
      const { data } = await request();
      if (data && data.url) doRedirect(data.url);
      else setError("Le service de paiement est momentanément indisponible. Réessayez.");
    } catch (e) {
      setError(mapError(e));
    } finally {
      inFlightRef.current = false;
      setPending(null);
    }
  }, [doRedirect]);

  const startSubscription = (plan) =>
    runAction(`sub:${plan}`, () =>
      api.post(`/organizations/${orgId}/billing/checkout/subscription`, {
        plan,
        interval: billingInterval,
      }));

  const buyCredits = (pack) =>
    runAction(`pack:${pack}`, () =>
      api.post(`/organizations/${orgId}/billing/checkout/credits`, { pack }));

  const openPortal = () =>
    runAction("portal", () => api.post(`/organizations/${orgId}/billing/portal`));

  const busy = Boolean(pending);

  // ---- status rendering --------------------------------------------------
  const renderStatus = () => {
    if (status === null) {
      return <div className="text-sm text-slate-400" data-testid="billing-status-loading">{"Chargement de l'abonnement…"}</div>;
    }
    if (status === false) {
      return <div className="text-sm text-amber-600" data-testid="billing-status-error">{"Impossible de charger l'abonnement pour le moment."}</div>;
    }
    const planLabel = PLAN_FR[status.plan] || (status.plan ? String(status.plan) : "Aucune offre");
    const statusLabel = STATUS_FR[(status.subscription_status || "none")] || String(status.subscription_status || "Aucun abonnement");
    const intervalLabel = status.billing_interval === "annual" ? "Annuel" : status.billing_interval === "monthly" ? "Mensuel" : "—";
    const periodEnd = frDate(status.current_period_end);
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="billing-status">
        <StatusRow label="Offre active" value={planLabel} testid="billing-status-plan" />
        <StatusRow label="Statut" value={statusLabel} testid="billing-status-status" />
        <StatusRow label="Périodicité" value={intervalLabel} testid="billing-status-interval" />
        <StatusRow label="Fin de période" value={periodEnd || "—"} testid="billing-status-period-end" />
        {status.cancel_at_period_end ? (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="billing-status-cancel">
            Résiliation programmée à la fin de la période en cours.
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="billing-section">
      <div>
        <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Facturation &amp; abonnement</h2>
        <p className="mt-1 text-xs text-slate-400">
          Abonnement, crédits Lodge et gestion de la facturation. Les prix sont indiqués HT.
        </p>
      </div>

      {banner ? (
        <div
          data-testid="billing-banner"
          data-banner-type={banner.type}
          className={`mt-5 rounded-xl border px-4 py-3 text-sm ${
            banner.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-slate-200 bg-slate-50 text-slate-600"
          }`}
        >
          {banner.msg}
        </div>
      ) : null}

      {error ? (
        <div data-testid="billing-error" className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Current subscription state + Lodge Credits balance (readable by any member). */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Abonnement actuel</h3>
          {renderStatus()}
        </div>
        <div>
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Solde de crédits Lodge</h3>
          <OrgCreditBalance key={`billing-balance-${refreshKey}`} orgId={orgId} />
        </div>
      </div>

      {isDemo ? (
        <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="billing-demo-note">
          {"La facturation n'est pas disponible pour une organisation de démonstration."}
        </div>
      ) : !isManager ? (
        <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="billing-readonly-note">
          Seuls les propriétaires et administrateurs peuvent gérer la facturation.
        </div>
      ) : !billingAvailable ? (
        <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="billing-unavailable-note">
          La facturation en ligne sera prochainement disponible pour votre organisation.
        </div>
      ) : (
        <div className="mt-8 space-y-8" data-testid="billing-actions">
          {/* Subscription choices */}
          <div>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Choisir un abonnement</h3>
              <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" role="group" aria-label="Périodicité">
                <button
                  type="button"
                  data-testid="billing-interval-monthly"
                  onClick={() => setBillingInterval("monthly")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    billingInterval === "monthly" ? "bg-[#1E3D2B] text-white" : "text-slate-600"
                  }`}
                >
                  Mensuel
                </button>
                <button
                  type="button"
                  data-testid="billing-interval-annual"
                  onClick={() => setBillingInterval("annual")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    billingInterval === "annual" ? "bg-[#1E3D2B] text-white" : "text-slate-600"
                  }`}
                >
                  Annuel
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {PLANS.map((p) => (
                <div key={p.key} className="flex flex-col rounded-xl border border-slate-200 p-5" data-testid={`plan-card-${p.key}`}>
                  <span className="font-heading text-base font-semibold text-[#1E3D2B]">{p.name}</span>
                  <span className="mt-1 text-sm text-slate-600" data-testid={`plan-price-${p.key}`}>
                    {billingInterval === "annual" ? p.annual : p.monthly}
                  </span>
                  <button
                    type="button"
                    data-testid={`subscribe-${p.key}`}
                    disabled={busy}
                    onClick={() => startSubscription(p.key)}
                    className="mt-4 h-10 rounded-lg bg-[#3E7256] text-sm font-semibold text-white transition hover:bg-[#2F5A43] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {pending === `sub:${p.key}` ? "Redirection…" : "S'abonner"}
                  </button>
                </div>
              ))}

              {/* Enterprise: manual quote only, NEVER an automated Checkout. */}
              <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="plan-card-enterprise">
                <span className="font-heading text-base font-semibold text-[#1E3D2B]">Enterprise</span>
                <span className="mt-1 text-sm font-semibold text-slate-600" data-testid="enterprise-quote">Sur devis</span>
                <p className="mt-4 text-xs text-slate-400">Contactez-nous pour une offre sur mesure.</p>
              </div>
            </div>
          </div>

          {/* Additional credit packs (require an active paid subscription). */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Packs de crédits supplémentaires</h3>
            {!subscriptionActive ? (
              <p className="mt-1 text-xs text-amber-600" data-testid="packs-requires-subscription">
                Un abonnement actif est requis pour acheter des crédits.
              </p>
            ) : null}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {PACKS.map((pk) => (
                <div key={pk.key} className="flex items-center justify-between rounded-xl border border-slate-200 p-4" data-testid={`pack-card-${pk.key}`}>
                  <div>
                    <div className="text-sm font-semibold text-slate-700">{pk.label}</div>
                    <div className="text-xs text-slate-500" data-testid={`pack-price-${pk.key}`}>{pk.price}</div>
                  </div>
                  <button
                    type="button"
                    data-testid={`buy-credits-${pk.key}`}
                    disabled={busy || !subscriptionActive}
                    onClick={() => buyCredits(pk.key)}
                    className="h-9 rounded-lg border border-[#3E7256] px-3 text-xs font-semibold text-[#3E7256] transition hover:bg-[#ECF3EC] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {pending === `pack:${pk.key}` ? "…" : "Acheter des crédits"}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Manage billing (Customer Portal). */}
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-5">
            <div>
              <div className="text-sm font-semibold text-slate-700">Gérer la facturation</div>
              <div className="text-xs text-slate-500">Moyens de paiement, factures et résiliation.</div>
            </div>
            <button
              type="button"
              data-testid="manage-billing"
              disabled={busy}
              onClick={openPortal}
              className="h-10 rounded-lg bg-[#1E3D2B] px-4 text-sm font-semibold text-white transition hover:bg-[#152A1E] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pending === "portal" ? "Redirection…" : "Gérer la facturation"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
